/**
 * Safely invokes the global `widgetApi` function if it has been loaded.
 *
 * The live chat script injects `widgetApi` as a global. When the script
 * fails to load (e.g. local dev, VPN issues), the variable is never
 * declared and a direct reference throws a ReferenceError.
 *
 * This utility encapsulates the `typeof` guard so consumers can simply:
 *   callWidgetApi((api) => api.hide())
 *
 * @param {Function} onSuccess - Called with the widget API instance when available.
 * @param {Function} [onError] - Optional error handler. Defaults to no-op.
 * @returns {Promise|undefined} The promise chain if widgetApi is available, undefined otherwise.
 */
export function callWidgetApi(onSuccess, onError) {
  if (typeof window.widgetApi === 'undefined') {
    return undefined
  }

  const result = window.widgetApi()

  if (!result || typeof result.then !== 'function') {
    return undefined
  }

  return onError
    ? result.then(onSuccess).catch(onError)
    : result.then(onSuccess)
}
